/**
 * Moss & Bone design tokens (ADR-0020 §1/§2) — the single TypeScript source of
 * the palette for the Pixi render path. Every value mirrors a CSS custom
 * property in `theme.css` (same name, same hex). DOM consumes the CSS vars;
 * canvas consumes these ints. There is no second palette.
 *
 * Values are taken verbatim from the redesign handoff
 * (`design-handoff/.../mockups/hex6.css :root`). Do not freelance additions —
 * a new screen need that the token set can't express is an ADR amendment, not a
 * local literal.
 */

/** RGB integer colour as Pixi expects (`0xRRGGBB`). */
export type ColourHex = number;

/* ── type families (mirror theme.css --f-* tokens, HEX-101) ────────────────
   Pixi can't read CSS custom properties, so canvas text references these
   strings instead. Same families, same fallback chain as the DOM tokens; the
   fonts themselves load once via the Google Fonts <link> in index.html. */
export const FONT_DISPLAY = '"Cinzel", Georgia, serif';
export const FONT_SERIF = '"Spectral", Georgia, serif';
export const FONT_MONO = '"JetBrains Mono", "SFMono-Regular", ui-monospace, monospace';

/* ── surfaces (dark mossy) ─────────────────────────────────────────────────
   --void/--bg-0…3/--line/--line-soft. Deepest backdrop → button face → borders. */
export const VOID: ColourHex = 0x070a08;
export const BG_0: ColourHex = 0x0d1210;
export const BG_1: ColourHex = 0x141b16;
export const BG_2: ColourHex = 0x1d271e;
export const BG_3: ColourHex = 0x27331f;
export const LINE: ColourHex = 0x313f30;
export const LINE_SOFT: ColourHex = 0x283426;

/* ── moss ramp ─────────────────────────────────────────────────────────── */
export const MOSS_DIM: ColourHex = 0x3a4a37;
export const MOSS: ColourHex = 0x5b6e52;
export const MOSS_LT: ColourHex = 0x8a9b7c;

/* ── bone / parchment (text) ───────────────────────────────────────────── */
export const BONE: ColourHex = 0xddd2b8;
export const BONE_MID: ColourHex = 0xb3ad96;
export const BONE_DIM: ColourHex = 0x847f6c;
export const BONE_DEEP: ColourHex = 0x5c5848;

/* ── ember accent (the player / interactive) + gold ────────────────────── */
export const EMBER_DEEP: ColourHex = 0x7a4a22;
export const EMBER: ColourHex = 0xdb8c46;
export const EMBER_LT: ColourHex = 0xf0b873;
export const GOLD: ColourHex = 0xd6a447;

/* ── functional — card types (jewel tones, ADR-0015 §3) ────────────────── */
export type CardTypeKey = 'attack' | 'defense' | 'support' | 'recursive' | 'trap';
export const CARD_TYPE: Readonly<Record<CardTypeKey, ColourHex>> = {
  attack: 0xc8584a,
  defense: 0x5e88bb,
  support: 0x9a6fb4,
  recursive: 0x4ba091,
  trap: 0xc79443,
};

/* ── functional — rarity ───────────────────────────────────────────────── */
export type RarityKey = 'common' | 'rare' | 'epic' | 'legendary';
export const RARITY: Readonly<Record<RarityKey, ColourHex>> = {
  common: 0x8b9082,
  rare: 0x5e88bb,
  epic: 0xb063b8,
  legendary: 0xd6a447,
};

/* ── biome anchors (gradient seeds for the painterly world, ADR-0013 §10) ──
   These are the canonical biome colours; `faceColour` blends tile faces toward
   them. They supersede the earlier ad-hoc biome tints (HEX-100). */
export type BiomeKey = 'plain' | 'forest' | 'snow' | 'lake' | 'mountain';
export const BIOME_ANCHOR: Readonly<Record<BiomeKey, ColourHex>> = {
  plain: 0x6f7a4e,
  forest: 0x34472f,
  snow: 0xb9c2bd,
  lake: 0x2c4654,
  mountain: 0x4a4a48,
};

/* ── combat bars ───────────────────────────────────────────────────────── */
export const HP_ENEMY: ColourHex = 0xb4453c;
export const HP_SELF: ColourHex = 0x6f9a4d;
export const BLOCK: ColourHex = 0x6f93c2;
